import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Activity,
    Truck,
    FileText,
    Calendar,
    AlertTriangle,
    Database,
    Clock,
    Upload
} from 'lucide-react';
import { useApi } from '../hooks/useApi';

const FleetOverview = ({ showTitle = true, apiClient, uploadToStorage }) => {
    const { fetchStatsOverview } = useApi(apiClient);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mobileCardIndex, setMobileCardIndex] = useState(0);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    const [uploadType, setUploadType] = useState('Monthly');
    const cardsCarouselRef = useRef(null);
    const fileInputRef = useRef(null);

    const getCarouselStep = useCallback((container) => {
        if (!container) return 1;
        const firstCard = container.querySelector('[data-carousel-item="overview"]');
        if (!firstCard) return Math.max(container.clientWidth, 1);
        const styles = window.getComputedStyle(container);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return Math.max(firstCard.getBoundingClientRect().width + gap, 1);
    }, []);

    const handleCardsScroll = useCallback((e) => {
        const container = e.currentTarget;
        const step = getCarouselStep(container);
        const idx = Math.round(container.scrollLeft / step);
        setMobileCardIndex(Math.max(0, Math.min(4, idx)));
    }, [getCarouselStep]);

    const scrollToCard = useCallback((index) => {
        const container = cardsCarouselRef.current;
        if (!container) return;
        const step = getCarouselStep(container);
        container.scrollTo({ left: step * index, behavior: 'smooth' });
        setMobileCardIndex(index);
    }, [getCarouselStep]);

    const loadStats = useCallback(async (forceRefresh = false) => {
        try {
            const data = await fetchStatsOverview(forceRefresh);
            setStats(data);
        } catch (err) {
            console.error("Failed to load overview stats", err);
        } finally {
            setLoading(false);
        }
    }, [fetchStatsOverview]);

    useEffect(() => {
        loadStats(false);
    }, [loadStats]);

    const handleUploadClick = useCallback(() => {
        if (uploadingDocument) return;
        setUploadMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [uploadingDocument]);

    const handleDocumentUpload = useCallback(async (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        if (typeof uploadToStorage !== 'function') {
            setUploadMessage('Upload service is not available.');
            event.target.value = '';
            return;
        }

        try {
            setUploadingDocument(true);
            setUploadMessage('');
            const categoryDir = uploadType === 'Monthly'
                ? 'Reports/Monthly_Fleet_Documents'
                : 'Reports/Daily_Fleet_Documents';

            const uploadResult = await uploadToStorage(selectedFile, categoryDir);

            if (uploadResult?.unchanged) {
                setUploadMessage('This file is already in the system. No refresh was needed.');
                return;
            }

            if (uploadResult?.refresh_triggered) {
                await loadStats(true);
                setUploadMessage(`${uploadType} fleet document uploaded and data refreshed: ${selectedFile.name}`);
                return;
            }

            if (uploadResult?.ingestion_status === 'skipped_unsupported_type') {
                setUploadMessage('Uploaded to storage. File type not eligible for automatic data refresh.');
                return;
            }

            setUploadMessage(`${uploadType} fleet document uploaded: ${selectedFile.name}`);
        } catch (err) {
            const backendError = err?.response?.data?.detail || err?.message || 'Unknown upload error';
            setUploadMessage(`Upload failed: ${backendError}`);
        } finally {
            setUploadingDocument(false);
            event.target.value = '';
        }
    }, [uploadToStorage, loadStats]);

    if (loading) {
        return (
            <div className="mb-8 animate-fade-in-up">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Database className="w-5 h-5 mr-2 text-gray-600" />
                    Fleet Overview
                </h2>
                <div
                    ref={cardsCarouselRef}
                    onScroll={handleCardsScroll}
                    className="flex md:grid md:grid-cols-2 lg:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide -mx-1 px-1 pb-2"
                >
                    {[...Array(5)].map((_, i) => (
                        <div key={i} data-carousel-item="overview" className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm h-32 relative overflow-hidden min-w-[270px] sm:min-w-[320px] md:min-w-0 shrink-0 snap-start">
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100 to-transparent animate-shimmer"
                                style={{ animation: 'shimmer 2s infinite' }} />

                            {/* Skeleton content */}
                            <div className="relative z-10">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                                    <div className="w-16 h-6 bg-gray-200 rounded-full animate-pulse" />
                                </div>
                                <div className="space-y-2">
                                    <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
                                    <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex md:hidden items-center justify-center gap-2 mt-2">
                    {[...Array(5)].map((_, i) => (
                        <button
                            key={`overview-dot-loading-${i}`}
                            type="button"
                            onClick={() => scrollToCard(i)}
                            aria-label={`Go to overview card ${i + 1}`}
                            className={`h-2 rounded-full transition-all ${mobileCardIndex === i ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
                        />
                    ))}
                </div>

                {/* Add shimmer keyframes to the component */}
                <style jsx>{`
                    @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                `}</style>
            </div>
        );
    }

    if (!stats) return null;

    const cards = [
        {
            title: 'Unplanned maintenance',
            value: stats.total_issues.toLocaleString(),
            icon: AlertTriangle,
            color: 'text-red-500',
            bg: 'bg-red-50',
            sub: 'Total Events'
        },
        {
            title: 'Active Units',
            value: stats.unit_count,
            icon: Truck,
            color: 'text-blue-500',
            bg: 'bg-blue-50',
            sub: 'TK Trucks'
        },
        {
            title: stats.monthly_trend ? `${stats.monthly_trend.m1_month} (${stats.monthly_trend.direction.toUpperCase()} vs Last Month)` : 'Stable',
            value: stats.monthly_trend ? `${stats.monthly_trend.m1_count.toLocaleString()}` : stats.unique_issue_types.toLocaleString(),
            icon: Activity,
            color: 'text-purple-500',
            bg: 'bg-purple-50',
            sub: 'Trend'
        },
        {
            title: 'Top Truck (Last Month)',
            value: stats.top_truck_last_month,
            icon: Calendar,
            color: 'text-orange-500',
            bg: 'bg-orange-50',
            sub: 'Most Affected'
        },
        {
            title: 'Top Issue (Last Month)',
            value: stats.top_issue_last_month,
            icon: FileText,
            color: 'text-green-500',
            bg: 'bg-green-50',
            sub: 'Most Frequent'
        }
    ];

    return (
        <div
            className={`${showTitle ? 'mb-8' : ''} animate-fade-in-up`}
            data-fleet-overview-loaded={stats !== null && !loading}
        >
            {showTitle && (
                <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: 'var(--accent-text)' }}>
                    <Database className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
                    Fleet Overview
                </h2>
            )}
            <div
                ref={cardsCarouselRef}
                onScroll={handleCardsScroll}
                className="flex md:grid md:grid-cols-2 lg:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide -mx-1 px-1 pb-2"
            >
                {cards.map((card, idx) => (
                    <div
                        key={idx}
                        data-carousel-item="overview"
                        className="card p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-200 transition-shadow duration-200 min-w-[270px] sm:min-w-[320px] md:min-w-0 shrink-0 snap-start"
                        style={{
                            background: 'var(--card-bg)',
                            borderRadius: 'var(--radius-lg)'
                        }}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className={`p-3 rounded-xl ${card.bg}`}>
                                <card.icon className={`w-6 h-6 ${card.color}`} />
                            </div>
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {card.sub}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold mb-2 tabular-nums" style={{ color: 'var(--accent-text)' }}>{card.value}</h3>
                            <p className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>{card.title}</p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex md:hidden items-center justify-center gap-2 mt-2">
                {cards.map((_, i) => (
                    <button
                        key={`overview-dot-${i}`}
                        type="button"
                        onClick={() => scrollToCard(i)}
                        aria-label={`Go to overview card ${i + 1}`}
                        className={`h-2 rounded-full transition-all ${mobileCardIndex === i ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
                    />
                ))}
            </div>

            {/* Date Range Footer */}
            <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--card-border)' }}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
                    <div className="flex items-center text-sm" style={{ color: 'var(--accent-text-subtle)' }}>
                        <Clock className="w-4 h-4 mr-2" />
                        <span className="font-medium">
                            Data Range: {new Date(stats.date_range_start).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })} - {new Date(stats.date_range_end).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })}
                        </span>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-2 text-sm">
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            accept=".xlsx,.xls,.csv,.pdf,.doc,.docx,.txt"
                            onChange={handleDocumentUpload}
                        />
                        <div className="flex items-center gap-2">
                            <select
                                value={uploadType}
                                onChange={(e) => setUploadType(e.target.value)}
                                disabled={uploadingDocument}
                                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="Monthly">Monthly</option>
                                <option value="Daily">Daily</option>
                            </select>
                            <button
                                type="button"
                                onClick={handleUploadClick}
                                disabled={uploadingDocument}
                                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${uploadingDocument
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                            >
                                <Upload className="w-3.5 h-3.5" />
                                {uploadingDocument ? 'Uploading...' : `Upload Document`}
                            </button>
                        </div>
                        <p className="text-xs" style={{ color: 'var(--accent-text-subtle)' }}>
                            Use this to upload the latest {uploadType.toLowerCase()} fleet report.
                        </p>
                    </div>
                </div>
                {uploadMessage && (
                    <p
                        className="text-xs mb-3"
                        style={{ color: uploadMessage.startsWith('Upload failed') ? '#b91c1c' : 'var(--accent-text-subtle)' }}
                    >
                        {uploadMessage}
                    </p>
                )}
                {/* Monthly Tabs - Temporarily disabled */}
                {/* <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Months Covered</h4>
                    <div className="flex flex-wrap gap-2">
                        {generateMonthList().map((month, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleMonthClick(month)}
                                className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-md transition-colors border border-blue-100"
                            >
                                {month}
                            </button>
                        ))}
                    </div>
                </div> */}
            </div>

        </div>
    );
};

export default FleetOverview;
